import React, { useState, useEffect } from 'react';
import { X, Users, UserCheck } from 'lucide-react';
import { UserListItem } from '../types';
import { getAllUsers, assignExamToUsers, assignCourseToUsers } from '../services/api';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'exam' | 'course';
  itemId: number;
  itemName: string;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({ isOpen, onClose, type, itemId, itemName }) => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllUsers();
      // Filter out admin users for assignment
      setUsers(response.data.filter(user => !user.is_admin));
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredUsers = users.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(user => user.id));
    }
  };

  const handleAssign = async () => {
    if (selectedUserIds.length === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (type === 'exam') {
        await assignExamToUsers(itemId, { userIds: selectedUserIds });
      } else {
        await assignCourseToUsers(itemId, { userIds: selectedUserIds });
      }
      
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to assign ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Assign {type === 'exam' ? 'Exam' : 'Course'}: {itemName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-100">
            {error}
          </div>
        )}

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="mb-4 flex justify-between items-center">
          <button
            onClick={handleSelectAll}
            className="text-blue-400 hover:text-blue-300 flex items-center"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            {selectedUserIds.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
          </button>
          <span className="text-sm text-gray-400">
            {selectedUserIds.length} of {filteredUsers.length} users selected
          </span>
        </div>

        <div className="max-h-96 overflow-y-auto mb-4">
          {loading ? (
            <div className="text-center py-4">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-4 text-gray-400">No users found</div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`p-3 rounded border cursor-pointer transition-colors ${
                    selectedUserIds.includes(user.id)
                      ? 'bg-blue-500/20 border-blue-500'
                      : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                  }`}
                  onClick={() => handleUserToggle(user.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-gray-400">{user.email}</div>
                    </div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedUserIds.includes(user.id)
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-400'
                    }`}>
                      {selectedUserIds.includes(user.id) && (
                        <UserCheck className="h-3 w-3 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || selectedUserIds.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Assigning...' : `Assign to ${selectedUserIds.length} User${selectedUserIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal; 